import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import CircularProgress from '@mui/material/CircularProgress';
import LanguageRoundedIcon from '@mui/icons-material/LanguageRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import useLanguageSwitcher from './useLanguageSwitcher';
import MenuHeading from './MenuHeading';
import { tokens as c } from '../../theme/tokens';

/**
 * The language items as menu rows: a tick on the current one, the
 * language's own name, its English name at the end. Used inside
 * LanguageMenu and inside the shell's account menu on phones. Call it as a
 * function inside a <Menu> (renderLanguageItems({ switcher })) so the menu's
 * keyboard handling sees each row.
 */
export function renderLanguageItems({ switcher, onPicked, dense = false }) {
  const { languages, current, pendingLocale, switchTo } = switcher;
  return languages.map((lang) => {
    const selected = current && current.langLocale === lang.langLocale;
    const busy = pendingLocale === lang.langLocale;
    return (
      <MenuItem
        key={lang.langLocale}
        role="menuitemradio"
        aria-checked={selected ? 'true' : 'false'}
        lang={lang.langLocale}
        onClick={() => {
          if (onPicked) onPicked();
          if (!selected) switchTo(lang.langLocale);
        }}
        sx={{
          minHeight: 44,
          mx: dense ? 0 : 0.75,
          px: 1.75,
          gap: 1.25,
          borderRadius: '8px',
          bgcolor: selected ? c.ACCENT_TINT : 'transparent',
          '&:hover': { bgcolor: selected ? c.ACCENT_TINT : c.SURFACE_ALT },
        }}
      >
        <Box component="span" sx={{ width: 18, display: 'inline-flex', flex: 'none', color: c.ACCENT_TEXT }} aria-hidden="true">
          {busy ? <CircularProgress size={16} sx={{ color: c.ACCENT }} /> : selected ? <CheckRoundedIcon sx={{ fontSize: 18 }} /> : null}
        </Box>
        <Box component="span" sx={{ fontSize: 15, lineHeight: 1.4, fontWeight: selected ? 700 : 600, color: selected ? c.ACCENT_TEXT : c.INK }}>
          {lang.name}
        </Box>
        {lang.englishName && lang.englishName !== lang.name ? (
          <Box component="span" lang="en" sx={{ ml: 'auto', pl: 2, fontSize: 13, color: c.INK_2 }}>
            {lang.englishName}
          </Box>
        ) : null}
      </MenuItem>
    );
  });
}

/**
 * The language switch.
 *   <LanguageMenu variant="pill" />   white pill for the orange app bar (signed-in shell)
 *   <LanguageMenu variant="header" /> text button for the public header
 * Renders nothing when only one language exists.
 */
export default function LanguageMenu({ variant = 'pill', sx }) {
  const { t } = useTranslation();
  const switcher = useLanguageSwitcher();
  const [anchor, setAnchor] = useState(null);
  const { languages, current, pendingLocale, signedIn } = switcher;
  if (!languages || languages.length < 2) return null;
  const open = Boolean(anchor);
  const label = current ? current.name : t('lang');
  const busy = Boolean(pendingLocale);
  const menuTitle = variant === 'header' ? t('lang') : t('lang_menu_title', { defaultValue: 'Portal language' });

  const trigger =
    variant === 'header' ? (
      <Button
        color="inherit"
        onClick={(e) => setAnchor(e.currentTarget)}
        aria-haspopup="menu"
        aria-expanded={open ? 'true' : 'false'}
        aria-label={`${t('lang')}: ${label}`}
        startIcon={<LanguageRoundedIcon />}
        endIcon={busy ? <CircularProgress size={16} color="inherit" /> : <KeyboardArrowDownRoundedIcon />}
        sx={{ minHeight: 44, fontSize: 15, fontWeight: 500, ...sx }}
      >
        {label}
      </Button>
    ) : (
      <ButtonBase
        onClick={(e) => setAnchor(e.currentTarget)}
        aria-haspopup="menu"
        aria-expanded={open ? 'true' : 'false'}
        aria-label={`${t('lang')}: ${label}`}
        sx={{
          height: 40,
          pl: 1.5,
          pr: 1.25,
          gap: 1,
          borderRadius: '20px',
          bgcolor: c.WHITE,
          color: c.INK,
          fontSize: 14,
          fontWeight: 600,
          whiteSpace: 'nowrap',
          boxShadow: open ? '0 0 0 3px rgba(255,255,255,0.45)' : 'none',
          '&.Mui-focusVisible': { boxShadow: '0 0 0 3px rgba(255,255,255,0.75)' },
          '@media (pointer: coarse)': { height: 44 },
          ...sx,
        }}
      >
        <LanguageRoundedIcon sx={{ fontSize: 18, color: c.ACCENT_TEXT }} aria-hidden="true" />
        <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
          {label}
        </Box>
        {busy ? (
          <CircularProgress size={14} sx={{ color: c.ACCENT }} aria-hidden="true" />
        ) : (
          <KeyboardArrowDownRoundedIcon sx={{ fontSize: 18, color: c.INK_2, transform: open ? 'rotate(180deg)' : 'none' }} aria-hidden="true" />
        )}
      </ButtonBase>
    );

  return (
    <>
      {trigger}
      <Menu
        anchorEl={anchor}
        open={open}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { width: 260, maxWidth: 'calc(100vw - 24px)', mt: 1, borderRadius: '12px', py: 0.75 } }}
        MenuListProps={{ 'aria-label': menuTitle, sx: { py: 0 } }}
      >
        <MenuHeading sx={{ px: 2.5, pt: 1, pb: 0.75, fontSize: 12, fontWeight: 600, lineHeight: 1.5, color: c.TEXT_3 }}>
          {menuTitle}
        </MenuHeading>
        {renderLanguageItems({ switcher, onPicked: () => setAnchor(null) })}
        {signedIn ? (
          <MenuHeading
            sx={{
              display: 'flex',
              gap: 1,
              alignItems: 'flex-start',
              mt: 0.75,
              px: 2,
              pt: 1.25,
              pb: 0.75,
              borderTop: `1px solid ${c.OUTLINE}`,
              fontSize: 13,
              lineHeight: 1.45,
              color: c.INK_2,
            }}
          >
            <InfoOutlinedIcon sx={{ fontSize: 16, mt: '1px', flex: 'none' }} aria-hidden="true" />
            <span>{t('lang_saved_note', { defaultValue: 'Saved to your profile, so the portal opens in this language next time.' })}</span>
          </MenuHeading>
        ) : null}
      </Menu>
    </>
  );
}
